import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { SERVICE_OPTIONS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InquiryForm = ({
  service = "general",
  showServiceSelect = false,
  plan = "",
  submitLabel = "Send Inquiry",
  testidPrefix = "contact-form",
}) => {
  const empty = {
    name: "",
    email: "",
    phone: "",
    company: "",
    service,
    message: plan ? `I'm interested in the ${plan} package.` : "",
    website: "",
  };
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setForm((f) => ({ ...f, service }));
  }, [service]);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Message sent — our team will reach out shortly.");
      setForm({ ...empty, message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again or reach us on WhatsApp."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`${testidPrefix}-form`}>
      <input
        type="text"
        value={form.website}
        onChange={update("website")}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <input
          required
          minLength={2}
          placeholder="Your name *"
          value={form.name}
          onChange={update("name")}
          className="spark-input"
          data-testid={`${testidPrefix}-name-input`}
        />
        <input
          required
          type="email"
          placeholder="Email address *"
          value={form.email}
          onChange={update("email")}
          className="spark-input"
          data-testid={`${testidPrefix}-email-input`}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <input
          placeholder="Phone (optional)"
          value={form.phone}
          onChange={update("phone")}
          className="spark-input"
          data-testid={`${testidPrefix}-phone-input`}
        />
        <input
          placeholder="Company (optional)"
          value={form.company}
          onChange={update("company")}
          className="spark-input"
          data-testid={`${testidPrefix}-company-input`}
        />
      </div>
      {showServiceSelect && (
        <select
          value={form.service}
          onChange={update("service")}
          className="spark-input appearance-none"
          data-testid={`${testidPrefix}-service-select`}
        >
          {SERVICE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value} className="bg-[#131316]">
              {o.label}
            </option>
          ))}
        </select>
      )}
      <textarea
        required
        minLength={10}
        rows={4}
        placeholder="Tell us about your project *"
        value={form.message}
        onChange={update("message")}
        className="spark-input resize-none"
        data-testid={`${testidPrefix}-message-input`}
      />
      <button
        type="submit"
        disabled={sending}
        data-testid={`${testidPrefix}-submit-button`}
        className="flex w-full items-center justify-center gap-2 rounded-full bg-[#D9A92E] px-6 py-3.5 text-sm font-semibold text-[#09090B] shadow-[0_0_25px_rgba(217,169,46,0.4)] transition-all duration-300 hover:bg-[#E8C05A] hover:shadow-[0_0_35px_rgba(217,169,46,0.6)] disabled:opacity-60"
      >
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {sending ? "Sending..." : submitLabel}
      </button>
    </form>
  );
};

export default InquiryForm;
